import { supabaseStorageCompat as supabase } from "./fileStorage";

import { db } from "./dbClient";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { callAI } from "./aiClient";
import { printToPDF, sanitize } from "./exportUtils";

// ─── Load all module data (shared with KPIGraphGenerator) ────────────────────
async function loadAllModuleData(companyId) {
  const safe = (p) => Promise.resolve(p)
    .then(r => ({ data: r?.data ?? [], error: r?.error ?? null }))
    .catch(() => ({ data: [], error: null }));
  const byCompany = (q) => companyId ? q.eq("company_id", companyId) : q;
  const lsGet = (key, fallback = []) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
    catch { return fallback; }
  };

  const [
    { data: cases },
    { data: sarDrafts },
    { data: eddReviews },
    { data: kycSubmissions },
    { data: sanctionsScreenings },
    { data: trainingModules },
    { data: trainingProgress },
    { data: transactions },
    { data: ctrFilings },
  ] = await Promise.all([
    safe(byCompany(db.from("cases").select("*"))),
    safe(byCompany(db.from("sar_drafts").select("*"))),
    safe(byCompany(db.from("edd_reviews").select("*"))),
    safe(byCompany(db.from("kyc_submissions").select("*"))),
    safe(byCompany(db.from("sanctions_screening").select("*"))),
    safe(byCompany(db.from("training_modules").select("*"))),
    safe(byCompany(db.from("training_progress").select("*"))),
    safe(byCompany(db.from("transactions").select("*"))),
    safe(byCompany(db.from("ctr_filings").select("*"))),
  ]);

  const tmAlerts = lsGet("crossregguard_tm_alerts");
  const count = (arr, key) => (arr || []).reduce((a, x) => {
    const v = x?.[key] || "Unknown"; a[v] = (a[v] || 0) + 1; return a;
  }, {});
  const pct = (n, total) => total ? `${Math.round(n / total * 100)}%` : "0%";

  const casesArr  = cases  || [];
  const sarArr    = sarDrafts || [];
  const eddArr    = eddReviews || [];
  const kycArr    = kycSubmissions || [];
  const sanctArr  = sanctionsScreenings || [];
  const trainMods = trainingModules || [];
  const trainProg = trainingProgress || [];
  const txArr     = transactions || [];
  const ctrArr    = ctrFilings || [];

  const completedTrain = trainProg.filter(p => p.quiz_passed === true);

  return {
    cases: {
      total: casesArr.length,
      open: casesArr.filter(c => c.status === "open").length,
      closed: casesArr.filter(c => c.status === "closed").length,
      byStatus: count(casesArr, "status"),
      byPriority: count(casesArr, "priority"),
      byRisk: count(casesArr, "risk_rating"),
      recentCount: casesArr.filter(c => {
        try { return new Date(c.created_at) > new Date(Date.now() - 30 * 86400000); }
        catch { return false; }
      }).length,
    },
    sar: {
      total: sarArr.length,
      filed: sarArr.filter(s => s.status === "filed" || s.filing_date).length,
      pending: sarArr.filter(s => s.status === "pending" || s.status === "draft").length,
      byActivityType: count(sarArr, "activity_type"),
    },
    ctr: {
      total: ctrArr.length,
      filed: ctrArr.filter(c => c.status === "filed").length,
      pending: ctrArr.filter(c => c.status !== "filed").length,
    },
    edd: {
      total: eddArr.length,
      highRisk: eddArr.filter(e => e.risk_level === "high" || e.risk_level === "High").length,
      byStatus: count(eddArr, "status"),
    },
    kyc: {
      total: kycArr.length,
      approved: kycArr.filter(k => k.status === "approved").length,
      pending: kycArr.filter(k => k.status === "pending" || k.status === "under_review").length,
      rejected: kycArr.filter(k => k.status === "rejected").length,
      highRisk: kycArr.filter(k => k.risk_rating === "high" || k.risk_rating === "High").length,
      approvalRate: pct(kycArr.filter(k => k.status === "approved").length, kycArr.length),
    },
    sanctions: {
      total: sanctArr.length,
      hits: sanctArr.filter(s => s.result === "hit" || s.result === "match").length,
      clear: sanctArr.filter(s => s.result === "clear" || s.result === "no_match").length,
      falsePositives: sanctArr.filter(s => s.review_status === "false_positive").length,
      hitRate: pct(sanctArr.filter(s => s.result === "hit" || s.result === "match").length, sanctArr.length),
    },
    training: {
      modules: trainMods.length,
      completions: completedTrain.length,
      completionRate: pct(completedTrain.length, trainProg.length),
    },
    transactions: {
      total: txArr.length,
      totalVolume: txArr.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0).toFixed(2),
      flagged: txArr.filter(t => t.flagged || t.status === "flagged").length,
    },
    transactionMonitoring: {
      total: tmAlerts.length,
      open: tmAlerts.filter(a => a.status === "open").length,
      falsePositives: tmAlerts.filter(a => a.status === "false_positive").length,
    },
  };
}

// ─── Constants ──────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  "AML Annual Report",
  "SAR Summary Report",
  "CTR Summary Report",
  "Fraud Activity Report",
  "Transaction Activity Report",
  "Board of Directors Report",
  "Internal Audit Report",
  "External Audit Report",
  "KYC / Onboarding Summary",
  "Sanctions Screening Report",
  "Training & Awareness Report",
  "EDD Summary Report",
  "BSA/AML Risk Assessment",
  "Other",
];

const PERIODS = ["Monthly", "Quarterly", "Semi-Annual", "Annual", "Ad-Hoc"];
const STATUSES = ["draft", "review", "submitted", "filed"];

const STATUS_COLORS = {
  draft:     { bg: "rgba(138,155,181,0.1)", color: "#8A9BB5",  border: "rgba(138,155,181,0.3)" },
  review:    { bg: "rgba(255,140,0,0.1)",   color: "#FF8C00",  border: "rgba(255,140,0,0.3)" },
  submitted: { bg: "rgba(0,212,255,0.1)",   color: "#00D4FF",  border: "rgba(0,212,255,0.3)" },
  filed:     { bg: "rgba(0,230,118,0.1)",   color: "#00E676",  border: "rgba(0,230,118,0.3)" },
};

function getTranslatedReportType(type, t) {
  const typeMap = {
    "AML Annual Report": "regulatory.reportTypes.aml_annual",
    "SAR Summary Report": "regulatory.reportTypes.sar_summary",
    "CTR Summary Report": "regulatory.reportTypes.ctr_summary",
    "Fraud Activity Report": "regulatory.reportTypes.fraud_activity",
    "Transaction Activity Report": "regulatory.reportTypes.transaction_activity",
    "Board of Directors Report": "regulatory.reportTypes.board_report",
    "Internal Audit Report": "regulatory.reportTypes.internal_audit",
    "External Audit Report": "regulatory.reportTypes.external_audit",
    "KYC / Onboarding Summary": "regulatory.reportTypes.kyc_onboarding",
    "Sanctions Screening Report": "regulatory.reportTypes.sanctions_screening",
    "Training & Awareness Report": "regulatory.reportTypes.training_awareness",
    "EDD Summary Report": "regulatory.reportTypes.edd_summary",
    "BSA/AML Risk Assessment": "regulatory.reportTypes.bsa_aml_risk",
    "Other": "regulatory.reportTypes.other",
  };
  return t(typeMap[type] || type);
}

function getTranslatedReportStatus(status, t) {
  const statusMap = {
    "draft": "regulatory.statuses.draft",
    "review": "regulatory.statuses.review",
    "submitted": "regulatory.statuses.submitted",
    "filed": "regulatory.statuses.filed",
  };
  return t(statusMap[status] || status);
}

// ─── Reusable components ────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: extraStyle = {} }) {
  const C = useTheme();
  const { t } = useTranslation();
  const base = {
    primary: { background: C.accent, color: C.bg, border: "none" },
    ghost:   { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    danger:  { background: "rgba(255,68,68,0.1)", color: C.danger, border: `1px solid ${C.danger}` },
    success: { background: "rgba(0,230,118,0.1)", color: C.success, border: `1px solid ${C.success}` },
    subtle:  { background: C.surfaceAlt || C.surface, color: C.textMid, border: `1px solid ${C.border}` },
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...base,
        padding: small ? "5px 12px" : "9px 18px",
        borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'DM Mono', monospace", fontWeight: 600,
        fontSize: small ? 11 : 13, opacity: disabled ? 0.45 : 1,
        transition: "opacity 0.15s, transform 0.1s",
        display: "inline-flex", alignItems: "center", gap: 6,
        whiteSpace: "nowrap", ...extraStyle,
      }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    >{children}</button>
  );
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{
      fontSize: 10, padding: "3px 8px", borderRadius: 4,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: "0.06em",
    }}>
      {getTranslatedReportStatus(status, t)}
    </span>
  );
}

// ─── Rich text editor ────────────────────────────────────────────────────────
function RichEditor({ value, onChange, readOnly = false, imperativeRef }) {
  const C = useTheme();
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const lastHtml = useRef(null);

  if (imperativeRef) {
    imperativeRef.current = {
      setHTML(html) {
        if (!editorRef.current) return;
        editorRef.current.innerHTML = html;
        lastHtml.current = html;
        onChange(html);
      },
      getHTML() { return editorRef.current?.innerHTML || ""; },
    };
  }

  useEffect(() => {
    if (editorRef.current && value !== lastHtml.current) {
      editorRef.current.innerHTML = value || "";
      lastHtml.current = value;
    }
  }, [value]);

  function handleInput() {
    const html = editorRef.current.innerHTML;
    lastHtml.current = html;
    onChange(html);
  }

  function ToolBtn({ label, cmd, arg }) {
    return (
      <button
        onMouseDown={e => {
          e.preventDefault();
          document.execCommand(cmd, false, arg);
          editorRef.current?.focus();
        }}
        title={label}
        style={{
          background: "transparent", border: "none", color: C.textMid,
          cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13,
        }}
        onMouseEnter={e => e.currentTarget.style.color = C.accent}
        onMouseLeave={e => e.currentTarget.style.color = C.textMid}
      >{label}</button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {!readOnly && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 2, padding: "6px 10px",
          borderBottom: `1px solid ${C.border}`, background: C.surface,
        }}>
          <ToolBtn label={t('regReporting.editorBold')} cmd="bold" />
          <ToolBtn label={t('regReporting.editorItalic')} cmd="italic" />
          <ToolBtn label={t('regReporting.editorUnderline')} cmd="underline" />
          <ToolBtn label={t('regReporting.editorH1')} cmd="formatBlock" arg="h2" />
          <ToolBtn label={t('regReporting.editorH2')} cmd="formatBlock" arg="h3" />
          <ToolBtn label={t('regReporting.editorParagraph')} cmd="formatBlock" arg="p" />
          <ToolBtn label={t('regReporting.editorBulletList')} cmd="insertUnorderedList" />
          <ToolBtn label={t('regReporting.editorNumberedList')} cmd="insertOrderedList" />
          <ToolBtn label={t('regReporting.editorHRule')} cmd="insertHorizontalRule" />
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        style={{
          flex: 1, overflowY: "auto", padding: "18px 22px",
          color: C.text, fontSize: 14, lineHeight: 1.7,
          fontFamily: "'DM Mono', monospace",
          outline: "none",
        }}
      />
    </div>
  );
}

// ─── New Report Modal ────────────────────────────────────────────────────────
function NewReportModal({ onClose, onCreate }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [type, setType] = useState(REPORT_TYPES[0]);
  const [period, setPeriod] = useState("Annual");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");

  function submit() {
    if (!title.trim()) return;
    onCreate({ title: title.trim(), type, period, periodStart, periodEnd });
    onClose();
  }

  const inp = {
    width: "100%", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px",
    fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box",
    marginBottom: 14,
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: 32, width: 500, fontFamily: "'DM Mono', monospace",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>
            {t('regReporting.newReportTitle')}
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelReportTitle')}</div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('regReporting.reportTitlePlaceholder')} style={inp} />

        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelReportType')}</div>
        <select value={type} onChange={e => setType(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
          {REPORT_TYPES.map(rt => <option key={rt} value={rt}>{getTranslatedReportType(rt, t)}</option>)}
        </select>

        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelReportingPeriod')}</div>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
          {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelPeriodStart')}</div>
            <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={inp} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelPeriodEnd')}</div>
            <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
          <Btn variant="ghost" onClick={onClose}>{t('regReporting.btnCancel')}</Btn>
          <Btn onClick={submit} disabled={!title.trim()}>{t('regReporting.btnCreateReport')}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Upload Modal ────────────────────────────────────────────────────────────
function UploadModal({ company, currentUser, onClose, onUploaded }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [type, setType] = useState(REPORT_TYPES[0]);
  const [period, setPeriod] = useState("Annual");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function upload() {
    if (!file || !company?.id) return;
    setUploading(true);
    setError("");
    try {
      const path = `${company.id}/regulatory-reports/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.from("case-documents").upload(path, file);
      if (upErr) throw upErr;
      onUploaded({ title: file.name.replace(/\.[^.]+$/, ""), type, period, storage_path: path, uploaded_by: currentUser?.email });
      onClose();
    } catch (e) {
      setError(e.message || t('regReporting.uploadFailed'));
    } finally {
      setUploading(false);
    }
  }

  const inp = {
    width: "100%", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px",
    fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box",
    marginBottom: 14,
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: 32, width: 460, fontFamily: "'DM Mono', monospace",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('regReporting.uploadModalTitle')}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelReportType')}</div>
        <select value={type} onChange={e => setType(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
          {REPORT_TYPES.map(rt => <option key={rt} value={rt}>{getTranslatedReportType(rt, t)}</option>)}
        </select>

        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 4 }}>{t('regReporting.labelReportingPeriod')}</div>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
          {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={e => setFile(e.target.files?.[0] || null)}
          style={{ ...inp, padding: "8px" }}
        />

        {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="ghost" onClick={onClose}>{t('regReporting.btnCancel')}</Btn>
          <Btn onClick={upload} disabled={!file || uploading}>
            {uploading ? t('regReporting.btnUploading') : t('regReporting.btnUpload')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── AI Panel ────────────────────────────────────────────────────────────────
function AIPanel({ report, company, onInsert }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (company?.id) {
      loadAllModuleData(company.id).then(d => setModuleData(d)).catch(() => {});
    }
  }, [company?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function send() {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setLoading(true);

    try {
      const dataCtx = moduleData ? `
LIVE PLATFORM DATA (use these real numbers in the report):
- Cases: ${moduleData.cases.total} total | ${moduleData.cases.open} open | ${moduleData.cases.closed} closed | ${moduleData.cases.recentCount} in last 30 days
- SARs: ${moduleData.sar.total} total | ${moduleData.sar.filed} filed | ${moduleData.sar.pending} pending
- CTRs: ${moduleData.ctr.total} total | ${moduleData.ctr.filed} filed | ${moduleData.ctr.pending} pending
- KYC/Onboarding: ${moduleData.kyc.total} submissions | ${moduleData.kyc.approved} approved (${moduleData.kyc.approvalRate}) | ${moduleData.kyc.rejected} rejected | ${moduleData.kyc.highRisk} high-risk
- EDD Reviews: ${moduleData.edd.total} total | ${moduleData.edd.highRisk} high-risk
- Sanctions: ${moduleData.sanctions.total} screenings | ${moduleData.sanctions.hits} hits (${moduleData.sanctions.hitRate}) | ${moduleData.sanctions.falsePositives} false positives
- Training: ${moduleData.training.modules} modules | ${moduleData.training.completions} completions | ${moduleData.training.completionRate} completion rate
- Transactions: ${moduleData.transactions.total} total | $${parseFloat(moduleData.transactions.totalVolume).toLocaleString()} volume | ${moduleData.transactions.flagged} flagged
- TM Alerts: ${moduleData.transactionMonitoring.total} total | ${moduleData.transactionMonitoring.open} open | ${moduleData.transactionMonitoring.falsePositives} false positives
` : "Note: live platform data not yet loaded.";

      const system = `You are a BSA/AML compliance report writer for ${company?.name || "a financial institution"}. You help draft professional regulatory reports with real data.

Current report: "${report?.title || "Regulatory Report"}"
Report type: ${report?.type || "General"}
Reporting period: ${report?.period || "Annual"} (${report?.period_start || ""} – ${report?.period_end || ""})
${dataCtx}

Guidelines:
- Use professional, regulatory-grade language appropriate for regulators, auditors, and the board
- Incorporate the live data above (specific numbers, percentages, trends)
- Structure reports with clear sections: Executive Summary, Key Findings, Statistics, Analysis, Conclusion
- Format output as clean HTML (use <h2>, <h3>, <p>, <ul>, <li>, <strong>, <table> tags)
- When asked to "generate" or "draft" a report, produce the full document ready to insert
- Be concise but thorough; compliance officers and examiners are the audience`;

      const reply = await callAI({
        system,
        messages: updated.map(m => ({ role: m.role, content: m.content })),
        maxTokens: 3000,
      });

      const assistantMsg = { role: "assistant", content: reply };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      width: 360, borderLeft: `1px solid ${C.border}`,
      display: "flex", flexDirection: "column", background: C.surface, flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14 }}>
          ✦ {t('regReporting.aiPanelHeading')}
        </div>
        <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
          {moduleData ? t('regReporting.aiDataLoaded') : t('regReporting.aiDataLoading')}
        </div>
      </div>

      {/* Quick prompts */}
      {messages.length === 0 && (
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ color: C.textDim, fontSize: 10, marginBottom: 8, letterSpacing: "0.08em" }}>{t('regReporting.quickActionsLabel')}</div>
          {[
            t('regReporting.quickPromptDraft'),
            t('regReporting.quickPromptExecSummary'),
            t('regReporting.quickPromptMetrics'),
            t('regReporting.quickPromptFindings'),
            t('regReporting.quickPromptRiskAssessment'),
          ].map(q => (
            <button key={q} onClick={() => { setInput(q); }} style={{
              display: "block", width: "100%", textAlign: "left", background: C.bg,
              border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid,
              fontSize: 11, padding: "7px 10px", marginBottom: 5,
              cursor: "pointer", fontFamily: "'DM Mono', monospace",
              transition: "border-color 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
            >{q}</button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            <div style={{ color: m.role === "user" ? C.accent : C.textMid, fontSize: 10, marginBottom: 4, letterSpacing: "0.08em" }}>
              {m.role === "user" ? t('regReporting.msgRoleUser') : t('regReporting.msgRoleAssistant')}
            </div>
            {m.role === "assistant" ? (
              <div>
                <div
                  style={{
                    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: "10px 12px", color: C.text, fontSize: 12, lineHeight: 1.6,
                    fontFamily: "'DM Mono', monospace",
                  }}
                  dangerouslySetInnerHTML={{ __html: m.content }}
                />
                <button
                  onClick={() => onInsert(m.content)}
                  style={{
                    marginTop: 6, background: "rgba(0,212,255,0.1)",
                    border: `1px solid rgba(0,212,255,0.3)`, borderRadius: 5,
                    color: C.accent, fontSize: 10, padding: "4px 10px",
                    cursor: "pointer", fontFamily: "'DM Mono', monospace",
                  }}
                >
                  ⬇ {t('regReporting.btnInsertIntoReport')}
                </button>
              </div>
            ) : (
              <div style={{
                background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 8,
                padding: "10px 12px", color: C.text, fontSize: 12, lineHeight: 1.6,
              }}>
                {m.content}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ color: C.textDim, fontSize: 12, fontStyle: "italic" }}>
            {t('regReporting.aiGenerating')}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", gap: 8 }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('regReporting.aiInputPlaceholder')}
            rows={2}
            style={{
              flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7,
              color: C.text, fontSize: 12, padding: "8px 10px", resize: "none",
              fontFamily: "'DM Mono', monospace", outline: "none",
            }}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            style={{
              background: C.accent, border: "none", borderRadius: 7,
              color: C.bg, padding: "0 14px", cursor: loading ? "not-allowed" : "pointer",
              fontSize: 16, opacity: loading ? 0.5 : 1,
            }}
          >↑</button>
        </div>
      </div>
    </div>
  );
}

// ─── Report Editor ───────────────────────────────────────────────────────────
function ReportEditor({ report, onBack, company, currentUser, onSaved }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log: auditLog } = useAuditLog(company, currentUser);
  const editorRef = useRef(null);
  const imperativeRef = useRef(null);
  const [content, setContent] = useState(report.content || "");
  const [status, setStatus] = useState(report.status || "draft");
  const [saving, setSaving] = useState(false);
  const [showAI, setShowAI] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  async function save(statusOverride) {
    setSaving(true);
    const resolvedStatus = statusOverride ?? status;
    if (statusOverride) setStatus(statusOverride);
    try {
      const html = imperativeRef.current?.getHTML() || content;
      const payload = {
        content: html,
        status: resolvedStatus,
      };
      if (report.id) {
        const { error } = await db.from("regulatory_reports").update(payload).eq("id", report.id);
        if (error) console.error('[Reg Reports] Update error:', error.message);
      }
      await auditLog({
        action: "regulatory_report_saved",
        module: "regulatory_reports",
        entityId: String(report.id || ""),
        entityName: report.title,
        newValue: { status: resolvedStatus },
      });
      setLastSaved(new Date());
      onSaved({ ...report, ...payload });
    } catch (e) {
      console.error("[save]", e);
    } finally {
      setSaving(false);
    }
  }

  function downloadPDF() {
    const html = imperativeRef.current?.getHTML() || content;
    const metaRows = [
      [t('regReporting.pdfLabelReportType'), report.type || ""],
      [t('regReporting.pdfLabelReportingPeriod'), report.period || ""],
      [t('regReporting.pdfLabelPeriod'), `${report.period_start || ""} – ${report.period_end || ""}`],
      [t('regReporting.pdfLabelStatus'), status.toUpperCase()],
      [t('regReporting.pdfLabelCreatedBy'), report.created_by || ""],
      [t('regReporting.pdfLabelLastUpdated'), report.updated_at ? new Date(report.updated_at).toLocaleDateString() : ""],
    ].map(([k, v]) => `<tr><td style="font-weight:600;width:160px">${k}</td><td>${v}</td></tr>`).join("");

    printToPDF(
      report.title,
      `<div class="section">
        <div class="section-title">${t('regReporting.pdfSectionDetails')}</div>
        <table>${metaRows}</table>
      </div>
      <div class="section">
        <div class="section-title">${t('regReporting.pdfSectionContent')}</div>
        <div style="font-size:11px;line-height:1.8">${html}</div>
      </div>`,
      company?.name
    );
  }

  const nextStatus = { draft: "review", review: "submitted", submitted: "filed" }[status];
  const nextLabel  = {
    draft: t('regReporting.btnSubmitForReview'),
    review: t('regReporting.btnMarkSubmitted'),
    submitted: t('regReporting.btnMarkFiled'),
  }[status];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 20px",
        borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18, padding: 4 }}
        >←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15, fontFamily: "'Syne', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {report.title}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
            <StatusBadge status={status} />
            <span style={{ color: C.textDim, fontSize: 11 }}>{report.type}</span>
            {report.period && <span style={{ color: C.textDim, fontSize: 11 }}>· {report.period}</span>}
            {lastSaved && <span style={{ color: C.textDim, fontSize: 10 }}>· {t('regReporting.lastSaved', { time: lastSaved.toLocaleTimeString() })}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Btn variant="ghost" small onClick={() => setShowAI(p => !p)}>
            {showAI ? t('regReporting.btnHideAI') : `✦ ${t('regReporting.btnShowAI')}`}
          </Btn>
          <Btn variant="ghost" small onClick={downloadPDF}>⬇ {t('regReporting.btnPDF')}</Btn>
          <Btn variant="subtle" small onClick={() => save()}>
            {saving ? t('regReporting.btnSaving') : t('regReporting.btnSave')}
          </Btn>
          {nextStatus && (
            <Btn variant="success" small onClick={() => save(nextStatus)}>
              {nextLabel}
            </Btn>
          )}
          {status === "filed" && (
            <span style={{ color: C.success, fontSize: 11, alignSelf: "center" }}>✓ {t('regReporting.statusFiled')}</span>
          )}
        </div>
      </div>

      {/* Editor + AI panel */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: C.bg }}>
          <RichEditor
            value={content}
            onChange={setContent}
            imperativeRef={imperativeRef}
          />
        </div>
        {showAI && (
          <AIPanel
            report={report}
            company={company}
            onInsert={html => {
              const cur = imperativeRef.current?.getHTML() || "";
              imperativeRef.current?.setHTML(cur + html);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function RegulatoryReportingPage({ company, currentUser, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log: auditLog } = useAuditLog(company, currentUser);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");

  // ── Load reports ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!company?.id) return;
    loadReports();
  }, [company?.id]);

  async function loadReports() {
    setLoading(true);
    try {
      const { data, error } = await db
        .from("regulatory_reports")
        .select("*")
        .eq("company_id", company.id)
        .order("updated_at", false);
      if (error) {
        console.error('[Reg Reports] Load error:', error.message);
        setReports([]);
      } else {
        setReports((data || []).sort((a, b) => (new Date(b.updated_at) - new Date(a.updated_at))));
      }
    } catch (e) {
      console.error('[Reg Reports] Load failed:', e.message);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }

  // ── Create report ─────────────────────────────────────────────────────────
  async function createReport({ title, type, period, periodStart, periodEnd }) {
    const payload = {
      company_id:   company.id,
      title:        sanitize(title),
      type,
      period,
      period_start: periodStart || null,
      period_end:   periodEnd || null,
      status:       "draft",
      content:      "",
      created_by:   currentUser?.email || "",
      created_at:   new Date().toISOString(),
      updated_at:   new Date().toISOString(),
    };
    const { data, error } = await db.from("regulatory_reports").insert(payload);
    if (error) { console.error('[Reg Reports] Create error:', error.message); return; }
    await auditLog({
      action: "regulatory_report_created",
      module: "regulatory_reports",
      entityId: String(data.id),
      entityName: title,
      newValue: { type, period, status: "draft" },
    });
    setReports(prev => [data, ...prev]);
    setActiveReport(data);
  }

  // ── Handle uploaded report ────────────────────────────────────────────────
  async function handleUploaded({ title, type, period, storage_path, uploaded_by }) {
    const payload = {
      company_id:   company.id,
      title:        sanitize(title),
      type,
      period,
      status:       "filed",
      content:      storage_path ? `Uploaded file: ${storage_path}` : "",
      created_by:   uploaded_by || currentUser?.email || "",
      created_at:   new Date().toISOString(),
      updated_at:   new Date().toISOString(),
    };
    const { data, error } = await db.from("regulatory_reports").insert(payload);
    if (error) { console.error('[Reg Reports] Upload error:', error.message); return; }
    if (data) setReports(prev => [data, ...prev]);
  }

  // ── Delete report ─────────────────────────────────────────────────────────
  async function deleteReport(id) {
    if (!window.confirm(t('regReporting.confirmDelete'))) return;
    try {
      const { error } = await db.from("regulatory_reports").delete().eq("id", id);
      if (error) {
        console.error('[Reg Reports] Delete error:', error.message);
        return;
      }
      setReports(prev => prev.filter(r => r.id !== id));
      if (activeReport?.id === id) setActiveReport(null);
    } catch (e) {
      console.error('[Reg Reports] Delete failed:', e.message);
    }
  }

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = reports.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterType !== "all" && r.type !== filterType) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ── If editing a report ───────────────────────────────────────────────────
  if (activeReport) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px)", overflow: "hidden" }}>
        <ReportEditor
          report={activeReport}
          company={company}
          currentUser={currentUser}
          onBack={() => setActiveReport(null)}
          onSaved={updated => {
            setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
            setActiveReport(updated);
          }}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700 }}>
            {t('regReporting.pageHeading')}
          </div>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>
            {t('regReporting.pageSubheading')}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="ghost" onClick={() => setShowUpload(true)}>⬆ {t('regReporting.btnUploadReport')}</Btn>
          <Btn onClick={() => setShowNew(true)}>+ {t('regReporting.btnNewReport')}</Btn>
        </div>
      </div>

      {/* Stats row */}
      {reports.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { label: t('regReporting.statTotalReports'), value: reports.length },
            { label: t('regReporting.statDrafts'), value: reports.filter(r => r.status === "draft").length, color: C.textDim },
            { label: t('regReporting.statInReview'), value: reports.filter(r => r.status === "review").length, color: "#FF8C00" },
            { label: t('regReporting.statSubmitted'), value: reports.filter(r => r.status === "submitted").length, color: C.accent },
            { label: t('regReporting.statFiled'), value: reports.filter(r => r.status === "filed").length, color: C.success },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
              padding: "12px 18px", minWidth: 110,
            }}>
              <div style={{ color: color || C.text, fontSize: 22, fontWeight: 700 }}>{value}</div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t('regReporting.searchPlaceholder')}
          style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
            color: C.text, fontSize: 12, padding: "8px 12px",
            fontFamily: "'DM Mono', monospace", outline: "none", width: 220,
          }}
        />
        <select
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
            color: C.text, fontSize: 12, padding: "8px 12px",
            fontFamily: "'DM Mono', monospace", outline: "none", cursor: "pointer",
          }}
        >
          <option value="all">{t('regReporting.filterAllStatuses')}</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
            color: C.text, fontSize: 12, padding: "8px 12px",
            fontFamily: "'DM Mono', monospace", outline: "none", cursor: "pointer",
          }}
        >
          <option value="all">{t('regReporting.filterAllTypes')}</option>
          {REPORT_TYPES.map(rt => <option key={rt} value={rt}>{getTranslatedReportType(rt, t)}</option>)}
        </select>
      </div>

      {/* Reports list */}
      {loading ? (
        <div style={{ color: C.textDim, fontSize: 13, padding: "40px 0", textAlign: "center" }}>{t('regReporting.loadingReports')}</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 0",
          border: `2px dashed ${C.border}`, borderRadius: 14,
        }}>
          <div style={{ color: C.textDim, fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ color: C.textMid, fontSize: 14, marginBottom: 6 }}>
            {reports.length === 0 ? t('regReporting.emptyStateNoReports') : t('regReporting.emptyStateNoMatch')}
          </div>
          <div style={{ color: C.textDim, fontSize: 12, marginBottom: 20 }}>
            {reports.length === 0
              ? t('regReporting.emptyStateHintCreate')
              : t('regReporting.emptyStateHintFilter')}
          </div>
          {reports.length === 0 && <Btn onClick={() => setShowNew(true)}>+ {t('regReporting.btnCreateFirstReport')}</Btn>}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(r => (
            <div
              key={r.id}
              onClick={() => setActiveReport(r)}
              style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
                padding: "16px 20px", cursor: "pointer", transition: "border-color 0.12s",
                display: "flex", alignItems: "center", gap: 16,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
            >
              {/* Icon */}
              <div style={{
                width: 40, height: 40, borderRadius: 9,
                background: C.accentGlow, border: `1px solid ${C.borderLight}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>
                {r.type?.includes("SAR") ? "◉" : r.type?.includes("CTR") ? "💵" : r.type?.includes("Board") ? "🏛" : r.type?.includes("Audit") ? "◬" : r.type?.includes("Training") ? "◭" : r.type?.includes("Sanctions") ? "🚫" : r.type?.includes("KYC") ? "🪪" : r.type?.includes("Risk") ? "⚠" : "📋"}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.title}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div style={{ display: "flex", gap: 12, color: C.textDim, fontSize: 11 }}>
                  <span>{r.type}</span>
                  {r.period && <span>· {r.period}</span>}
                  {r.period_start && r.period_end && <span>· {r.period_start} – {r.period_end}</span>}
                  {r.updated_at && <span>· {t('regReporting.updatedLabel', { date: new Date(r.updated_at).toLocaleDateString() })}</span>}
                  {r.created_by && <span>· {r.created_by}</span>}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <Btn variant="ghost" small onClick={() => setActiveReport(r)}>{t('regReporting.btnOpen')}</Btn>
                <Btn variant="danger" small onClick={() => deleteReport(r.id)}>{t('regReporting.btnDelete')}</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showNew && (
        <NewReportModal
          onClose={() => setShowNew(false)}
          onCreate={createReport}
        />
      )}
      {showUpload && (
        <UploadModal
          company={company}
          currentUser={currentUser}
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}
    </div>
  );
}
